import { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DevHubAlert,
  DevHubBox,
  DevHubCard,
  DevHubGrid,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { ApiPortalContext } from '../../contexts/ApiPortalContext';
import { ApiPortalDoc, DocType } from './ApiPortalTypes';
import ApiPortalTabbedPage from './ApiPortalTabbedPage';
import { Progress } from '@backstage/core-components';
import { slugifyHyphen } from '../../util/stringFormat';

type ApiPortalDocsProps = {
  isDataFeed?: boolean;
};

/**
 * A component to render an API Portal category's docs catalog.
 * @constructor
 * @param props the props of the component
 */
const ApiPortalDocs = (props: ApiPortalDocsProps) => {
  const navigate = useNavigate();
  const { categoryName, docType } = useParams<{
    categoryName: string;
    docType: DocType;
  }>();
  const [docs, setDocs] = useState<ApiPortalDoc[]>([]);
  const { org, loading, error, formattedOrgName } =
    useContext(ApiPortalContext);
  const selectedCategory = props.isDataFeed ? 'data-feed' : categoryName;
  const selectedDocType = props.isDataFeed ? DocType.Scenario : docType;

  const handleBackClick = () => {
    if (props.isDataFeed) {
      navigate(`/api-portals`);
    } else {
      navigate(`/api-portals/${org._normalizedName}/${selectedCategory}`);
    }
  };

  const onCardClick = (docName: string) => {
    if (props.isDataFeed) {
      navigate(
        `/api-portals/${org._normalizedName}/data-feed-management/${docName}`,
      );
    } else {
      navigate(
        `/api-portals/${org._normalizedName}/${selectedCategory}/${selectedDocType}/${docName}`,
      );
    }
  };

  useEffect(() => {
    if (org && org.categoriesPaged?.categories) {
      switch (selectedDocType) {
        case DocType.Event:
          setDocs(
            org.categoriesPaged?.categories?.find(
              x => slugifyHyphen(x.name) === selectedCategory,
            )?.apiEventsPaged?.events ?? [],
          );
          break;
        case DocType.Scenario:
          setDocs(
            org.categoriesPaged?.categories?.find(
              x => slugifyHyphen(x.name) === selectedCategory,
            )?.apiScenariosPaged?.scenarios ?? [],
          );
          break;
        default:
          break;
      }
    }
  }, [org, selectedCategory, selectedDocType]);

  const extractLastSection = (reference: string): string => {
    const lastSlashIndex = reference.lastIndexOf('/');
    const lastDotIndex = reference.lastIndexOf('.');
    if (
      lastSlashIndex === -1 ||
      lastDotIndex === -1 ||
      lastDotIndex < lastSlashIndex
    ) {
      throw new Error('Invalid reference string format');
    }
    return reference.substring(lastSlashIndex + 1, lastDotIndex);
  };

  const renderDocsCards = () => {
    return (
      <>
        <DevHubBox display="flex">
          <DevHubGrid container spacing={2}>
            {docs.map(doc => (
              <DevHubGrid item xs={12} md={6} lg={4} key={doc.title}>
                <DevHubCard
                  onClick={() => onCardClick(extractLastSection(doc.reference))}
                  density="compact"
                  consistentHeight
                  topBorder="custom"
                  topBorderColor="blue"
                  bodyPosition="middle"
                  subTitle=""
                  body={
                    <Fragment>
                      <DevHubBox
                        tbPadding="medium"
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <DevHubTypography variant="h5">
                          {doc.title}
                        </DevHubTypography>
                        <DevHubTypography>{doc.summary}</DevHubTypography>
                      </DevHubBox>
                    </Fragment>
                  }
                />
              </DevHubGrid>
            ))}
          </DevHubGrid>
        </DevHubBox>
      </>
    );
  };

  if (loading) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">
        Error loading API Portal organization data: {error.message}
      </DevHubAlert>
    );
  }

  return (
    <>
      {props.isDataFeed ? (
        renderDocsCards()
      ) : (
        <ApiPortalTabbedPage
          formattedOrgName={formattedOrgName}
          apiManagementContent={renderDocsCards()}
          handleBackClick={handleBackClick}
        />
      )}
    </>
  );
};

export default ApiPortalDocs;
